// ============================================
// 2026-10-17（标绘层 S2/S3）：3D 标绘渲染 + 选中/控制点编辑 + 绘制状态机
//  - 图元（S2）：点/文字/折线/面/矩形/圆；（S3）：椭圆/扇形/自由线/自由面/曲线 + 箭头族（直/细/曲线）
//  - 绘制：面板按钮进入绘制态（editorUiStore.annotationDrawKind）→ 地面捕捉层落点；
//          点击类=1~3 点即完成、折线/面/曲线=双击/「完成」结束；自由线/自由面=按住拖拽；
//          Esc 取消；落点方式=地面/模型表面（S3）；吸附=就近吸附模型包围盒顶点（S3）
//  - 编辑：选中显示控制点手柄（拖拽=实时更新 + 手势级撤销）；折线/面/曲线支持
//          双击边中点插入顶点、右键顶点删除（S3）；文本用 canvas 贴图 Sprite（离线可用）
//  - S3：数据绑定（文字显示设备点位值 + 阈值颜色）、生长/淡入动画、预览/发布同源（displayOnly 不渲染手柄/捕捉层）
// ============================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import useSceneStore, { type SceneAnnotation, type AnnotationKind } from '../../stores/sceneStore';
import useEditorUiStore from '../../stores/editorUiStore';
import { useSelectionStore } from '../../stores/selectionStore';
import { useDeviceDataStore } from '../../stores/deviceDataStore';
import { modelRefsRegistry } from '../../stores/modelRefsRegistry';
import { modelWorldBox } from '../../utils/modelBox';
import { beginGestureHistory, endGestureHistory } from '../../stores/dashboardHistory';
import {
  outlineFor,
  requiredClicks,
  isAreaKind,
  canEditVertices,
  kindLabel,
  needsExplicitFinish,
  type P3,
} from '../../utils/annotations/primitives';
import { rdpSimplify, sliceOutlineByProgress } from '../../utils/annotations/curveFit';
import { collectBoxSnapPoints, snapToCandidates } from '../../utils/annotations/grounding';

export const ANNOTATION_DEFAULTS = {
  lineColor: '#00d8ff',
  lineWidth: 2,
  dashed: false,
  fillColor: '#00d8ff',
  fillOpacity: 0.18,
  text: { content: '文字标注', fontSize: 16, color: '#ffffff', bg: 'rgba(4,8,18,0.55)' },
};

/** 2026-10-17：绘制态放行——已绘图形不拦截点击（否则面/线盖住捕捉层 → 无法继续落点） */
function annotationClickGuard<T>(e: any, fn: (e: T) => void) {
  if (useEditorUiStore.getState().annotationDrawKind) return false;
  e?.stopPropagation?.();
  fn(e);
  return true;
}

export function defaultStyleFor(kind: AnnotationKind) {
  const s = JSON.parse(JSON.stringify(ANNOTATION_DEFAULTS)) as SceneAnnotation['style'];
  if (kind === 'point') { s.fillColor = s.lineColor; }
  if (kind === 'arrowStraight' || kind === 'arrowFine' || kind === 'arrowCurve') { s.fillOpacity = 0.55; }
  return s;
}

// ---------- 生长/淡入动画（S3） ----------
/** 动画进度注册表（供调试桥/测试读取；1=完成） */
const animProgressRegistry = new Map<string, number>();
export function getAnnotationAnimProgress(id: string): number {
  return animProgressRegistry.get(id) ?? 1;
}
const ANIM_DURATION_MS = 1400;

function useAnnotationAnim(a: SceneAnnotation): number {
  const grow = !!a.animation?.grow;
  const fade = !!a.animation?.fade;
  const [progress, setProgress] = useState(1);
  useEffect(() => {
    if (!grow && !fade) {
      animProgressRegistry.set(a.id, 1);
      setProgress(1);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, Math.max(0.001, (t - start) / ANIM_DURATION_MS));
      animProgressRegistry.set(a.id, p);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    animProgressRegistry.set(a.id, 0.001);
    setProgress(0.001);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [a.id, grow, fade, a.visible]);
  useEffect(() => () => { animProgressRegistry.delete(a.id); }, [a.id]);
  return progress;
}

// ---------- S3 数据绑定：设备点位值 + 阈值颜色 ----------
/** 解析后的文字注册表（调试桥用） */
const resolvedTextRegistry = new Map<string, string>();
/** 阈值颜色命中注册表（调试桥用） */
const resolvedColorRegistry = new Map<string, string>();

function useAnnotationBinding(a: SceneAnnotation): { displayText: string | null; ruleColor: string | null } {
  const deviceId = a.binding?.deviceId;
  const pointKey = a.binding?.pointKey;
  const value = useDeviceDataStore((s) =>
    deviceId && pointKey ? s.deviceMap[deviceId]?.points?.[pointKey] : undefined,
  );
  const rulesKey = JSON.stringify(a.binding?.rules ?? []);
  return useMemo(() => {
    if (value === undefined || value === null) return { displayText: null, ruleColor: null };
    const raw = String(value);
    let displayText: string | null = null;
    if (a.kind === 'text') {
      const base = a.style.text?.content ?? '';
      displayText = base.includes('{value}') ? base.split('{value}').join(raw) : (base ? `${base} ${raw}` : raw);
    }
    const n = Number(value);
    let ruleColor: string | null = null;
    if (Number.isFinite(n)) {
      const rules = a.binding?.rules ?? [];
      const hit = rules.find((r) => n >= Number(r.min) && n <= Number(r.max));
      ruleColor = hit?.color ?? null;
    }
    return { displayText, ruleColor };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, rulesKey, a.kind, a.style.text?.content]);
}

/** 文本 → canvas 贴图 Sprite（多行；离线无字体依赖）；displayText 优先（绑定值解析后） */
function TextSprite({ a, selected, onSelect, displayText, colorOverride, opacity }: {
  a: SceneAnnotation;
  selected: boolean;
  onSelect: () => void;
  displayText?: string | null;
  colorOverride?: string | null;
  opacity?: number;
}) {
  const text = a.style.text ?? ANNOTATION_DEFAULTS.text;
  const content = displayText ?? String(text.content ?? '');
  const color = colorOverride ?? text.color;
  const texture = useMemo(() => {
    const lines = String(content).split('\n');
    const fontSize = 48;
    const pad = 16;
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d')!;
    ctx.font = `${fontSize}px sans-serif`;
    const w = Math.max(...lines.map((l) => ctx.measureText(l).width), 40) + pad * 2;
    const h = lines.length * (fontSize * 1.3) + pad * 2;
    c.width = Math.ceil(w);
    c.height = Math.ceil(h);
    const cx = c.getContext('2d')!;
    if (text.bg) { cx.fillStyle = text.bg; cx.fillRect(0, 0, c.width, c.height); }
    cx.font = `${fontSize}px sans-serif`;
    cx.fillStyle = color;
    cx.textBaseline = 'top';
    lines.forEach((l, i) => cx.fillText(l, pad, pad + i * fontSize * 1.3));
    const tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    return { tex, ratio: c.width / c.height };
  }, [content, color, text.bg]);

  useEffect(() => {
    resolvedTextRegistry.set(a.id, content);
    return () => { resolvedTextRegistry.delete(a.id); };
  }, [a.id, content]);

  const scale = Math.max(0.05, (text.fontSize ?? 16) / 16) * 2.2;
  const pos = a.points[0] ?? [0, 2, 0];
  return (
    <sprite position={pos} scale={[scale * texture.ratio, scale, 1]} onClick={(e) => annotationClickGuard(e, onSelect)}>
      <spriteMaterial map={texture.tex} transparent opacity={opacity ?? 1} depthWrite={false} color={selected ? '#ffd666' : '#ffffff'} />
    </sprite>
  );
}

/** 控制点手柄：拖拽更新点（地面/水平面），手势级撤销；右键删除（S3，可编辑图元） */
function PointHandle({
  point,
  index,
  annotationId,
  canDelete,
  onChanged,
}: {
  point: [number, number, number];
  index: number;
  annotationId: string;
  canDelete: boolean;
  onChanged: () => void;
}) {
  const { camera, gl } = useThree();
  const dragRef = useRef<{ plane: THREE.Plane; offset: THREE.Vector3; moved: boolean } | null>(null);

  const ndc = (e: PointerEvent) => {
    const rect = gl.domElement.getBoundingClientRect();
    return new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
  };
  const onMove = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc(e), camera);
    const hit = new THREE.Vector3();
    if (!ray.ray.intersectPlane(d.plane, hit)) return;
    d.moved = true;
    const next = hit.add(d.offset);
    const a = useSceneStore.getState().annotations.find((x) => x.id === annotationId);
    if (!a) return;
    const pts = a.points.map((p, i) => (i === index ? ([next.x, next.y, next.z] as [number, number, number]) : p));
    useSceneStore.getState().updateAnnotation(annotationId, { points: pts });
  }, [camera, annotationId, index]);
  const onUp = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    if (d?.moved) { endGestureHistory(); onChanged(); }
  }, [onMove, onChanged]);

  const onDown = (e: any) => {
    e.stopPropagation();
    useSelectionStore.getState().selectAnnotation(annotationId);
    beginGestureHistory();
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc(e.nativeEvent), camera);
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -point[1]); // 水平面 y=point.y
    const hit = new THREE.Vector3();
    if (!ray.ray.intersectPlane(plane, hit)) { endGestureHistory(); return; }
    dragRef.current = { plane, offset: new THREE.Vector3(point[0], point[1], point[2]).sub(hit), moved: false };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onDelete = (e: any) => {
    e.stopPropagation();
    e.nativeEvent?.preventDefault?.();
    if (!canDelete) return;
    const a = useSceneStore.getState().annotations.find((x) => x.id === annotationId);
    if (!a || a.points.length <= index) return;
    beginGestureHistory();
    useSceneStore.getState().updateAnnotation(annotationId, { points: a.points.filter((_, i) => i !== index) });
    endGestureHistory();
  };

  return (
    <group position={point}>
      <mesh onPointerDown={onDown} onContextMenu={onDelete}>
        <sphereGeometry args={[0.6, 12, 12]} />
        <meshBasicMaterial color="#ffd666" />
      </mesh>
      {/* 放大命中区（R3F 事件） */}
      <mesh onPointerDown={onDown} onContextMenu={onDelete} visible>
        <sphereGeometry args={[1.5, 8, 8]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}

/** 点到线段（xz 平面）距离 —— 双击插入顶点时找最近边 */
function segDistXZ(p: P3, a: P3, b: P3): number {
  const dx = b[0] - a[0];
  const dz = b[2] - a[2];
  const len2 = dx * dx + dz * dz;
  let t = len2 > 1e-9 ? ((p[0] - a[0]) * dx + (p[2] - a[2]) * dz) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p[0] - (a[0] + t * dx), p[2] - (a[2] + t * dz));
}

function AnnotationItem({ a, selected, showHandles }: { a: SceneAnnotation; selected: boolean; showHandles: boolean }) {
  const progress = useAnnotationAnim(a);
  const growOn = !!a.animation?.grow;
  const fadeOn = !!a.animation?.fade;
  const k = growOn || fadeOn ? progress : 1;
  const opacityK = fadeOn ? k : 1;
  const fillK = (growOn ? k : 1) * (fadeOn ? k : 1);
  const { displayText, ruleColor } = useAnnotationBinding(a);
  useEffect(() => {
    if (ruleColor) resolvedColorRegistry.set(a.id, ruleColor);
    else resolvedColorRegistry.delete(a.id);
  }, [a.id, ruleColor]);

  const outlineBase = useMemo(
    () => outlineFor(a.kind, a.points, a.style.arrow),
    [a.kind, a.points, a.style.arrow?.headSize, a.style.arrow?.tailWidth],
  );
  const outline = growOn && k < 1 ? sliceOutlineByProgress(outlineBase, k) : outlineBase;

  const onSelect = () => {
    useSceneStore.getState().selectModel(null);
    useSelectionStore.getState().selectAnnotation(a.id);
  };

  /** S3：双击边中点插入顶点（折线/面/曲线/自由线/自由面） */
  const onInsertVertex = (e: any) => {
    if (useEditorUiStore.getState().annotationDrawKind) return;
    if (!canEditVertices(a.kind)) return;
    e.stopPropagation();
    const p: P3 = [e.point.x, e.point.y, e.point.z];
    const pts = a.points;
    if (pts.length < 2) return;
    const pairs: Array<[number, number]> = [];
    for (let i = 0; i < pts.length - 1; i++) pairs.push([i, i + 1]);
    if (a.kind === 'polygon' || a.kind === 'freehandPolygon') pairs.push([pts.length - 1, 0]);
    let best: [number, number] | null = null;
    let bestD = Infinity;
    for (const pr of pairs) {
      const d = segDistXZ(p, pts[pr[0]], pts[pr[1]]);
      if (d < bestD) { bestD = d; best = pr; }
    }
    if (!best) return;
    const [i, j] = best;
    const mid: P3 = [(pts[i][0] + pts[j][0]) / 2, (pts[i][1] + pts[j][1]) / 2, (pts[i][2] + pts[j][2]) / 2];
    beginGestureHistory();
    const next = [...pts];
    next.splice(i + 1, 0, mid);
    useSceneStore.getState().updateAnnotation(a.id, { points: next });
    endGestureHistory();
  };

  const lineColor = selected ? '#ffd666' : (ruleColor ?? a.style.lineColor);
  const fill = selected ? '#ffd666' : (ruleColor ?? a.style.fillColor ?? a.style.lineColor);
  const baseFillOpacity = a.style.fillOpacity ?? 0.18;

  const shapeMesh = useMemo(() => {
    if (!isAreaKind(a.kind)) return null;
    const pts = outlineBase;
    if (pts.length < 3) return null;
    const shape = new THREE.Shape();
    shape.moveTo(pts[0][0], pts[0][2]);
    for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][2]);
    shape.closePath();
    const geo = new THREE.ShapeGeometry(shape);
    geo.rotateX(Math.PI / 2); // XY 平面 → XZ 平面
    return geo;
  }, [a.kind, outlineBase]);

  const y = a.points[0]?.[1] ?? 0;
  const minPointsToDelete = a.kind === 'polygon' || a.kind === 'freehandPolygon' ? 3 : 2;
  return (
    <group>
      {a.kind === 'point' && (
        <>
          <mesh position={a.points[0]} onClick={(e) => annotationClickGuard(e, onSelect)}>
            <sphereGeometry args={[0.6, 16, 16]} />
            <meshBasicMaterial color={lineColor} transparent opacity={opacityK} />
          </mesh>
          <mesh position={a.points[0]} onClick={(e) => annotationClickGuard(e, onSelect)}>
            <sphereGeometry args={[1.6, 8, 8]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
        </>
      )}
      {a.kind === 'text' && (
        <TextSprite a={a} selected={selected} onSelect={onSelect} displayText={displayText} colorOverride={ruleColor} opacity={opacityK} />
      )}
      {shapeMesh && (
        <mesh
          geometry={shapeMesh}
          position={[0, y + 0.02, 0]}
          onClick={(e) => annotationClickGuard(e, onSelect)}
          onDoubleClick={onInsertVertex}
        >
          <meshBasicMaterial color={fill} transparent opacity={Math.max(0.05, baseFillOpacity * fillK)} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
      )}
      {a.kind !== 'point' && a.kind !== 'text' && outline.length > 1 && (
        <Line
          points={outline}
          color={lineColor}
          lineWidth={a.style.lineWidth}
          dashed={a.style.dashed}
          dashSize={1.2}
          gapSize={0.8}
          transparent
          opacity={opacityK}
          onClick={(e: any) => annotationClickGuard(e, onSelect)}
          onDoubleClick={onInsertVertex}
        />
      )}
      {selected && showHandles && a.points.map((p, i) => (
        <PointHandle
          key={i}
          point={p}
          index={i}
          annotationId={a.id}
          canDelete={canEditVertices(a.kind) && a.points.length > minPointsToDelete}
          onChanged={() => void 0}
        />
      ))}
    </group>
  );
}

/** 绘制中已落点数（调试桥） */
let drawPointsRef: P3[] = [];

/** 绘制捕捉层 + 预览（绘制态才挂载）；S3：模型表面落点 + 吸附 + 自由线拖拽 + 新图元预览 */
function AnnotationDrawSurface() {
  const drawKind = useEditorUiStore((s) => s.annotationDrawKind);
  const planeMode = useEditorUiStore((s) => s.annotationPlaneMode);
  const snapEnabled = useEditorUiStore((s) => s.annotationSnap);
  const setDrawKind = useEditorUiStore((s) => s.setAnnotationDrawKind);
  const addAnnotation = useSceneStore((s) => s.addAnnotation);
  const [points, setPoints] = useState<P3[]>([]);
  const [cursor, setCursor] = useState<P3 | null>(null);
  const freehandRef = useRef<{ active: boolean; raw: P3[] } | null>(null);

  // 切换绘制模式/退出 → 清空进度
  useEffect(() => { setPoints([]); setCursor(null); freehandRef.current = null; }, [drawKind]);
  // 调试桥：绘制中点数
  useEffect(() => { drawPointsRef = points; }, [points]);
  useEffect(() => () => { drawPointsRef = []; }, []);

  /** 模型包围盒候选点（吸附用） */
  const snapCandidates = useCallback((): P3[] => {
    if (!snapEnabled) return [];
    const boxes = useSceneStore.getState().models
      .map((m) => {
        const box = modelWorldBox(m);
        if (!box) return null;
        return {
          min: box.min.toArray() as [number, number, number],
          max: box.max.toArray() as [number, number, number],
        };
      })
      .filter(Boolean) as Array<{ min: [number, number, number]; max: [number, number, number] }>;
    return collectBoxSnapPoints(boxes);
  }, [snapEnabled]);

  /** 落点解析：模型表面（planeMode=model，射线优先命中模型组）→ 地面 y=0.05；可选吸附 */
  const resolvePoint = (e: any): P3 => {
    const applySnap = (p: P3): P3 => {
      const cands = snapCandidates();
      return cands.length ? snapToCandidates(p, cands, 2.5) : p;
    };
    if (planeMode === 'model' && e?.ray) {
      try {
        const rc = new THREE.Raycaster();
        rc.ray.copy(e.ray as THREE.Ray);
        const objects: THREE.Object3D[] = [];
        modelRefsRegistry.getAll().forEach((h) => { if (h?.group) objects.push(h.group); });
        const hits = objects.length ? rc.intersectObjects(objects, true) : [];
        if (hits.length) {
          const p = hits[0].point;
          return applySnap([p.x, p.y, p.z]);
        }
      } catch { /* 射线失败回退地面 */ }
    }
    const gp = e?.point as THREE.Vector3 | undefined;
    return applySnap(gp ? [gp.x, gp.y, gp.z] : [0, 0, 0]);
  };

  const finish = useCallback((pts?: P3[]) => {
    const p = pts ?? points;
    const kind = drawKind;
    if (!kind || !p.length) { setDrawKind(null); return; }
    if ((kind === 'polyline' || kind === 'freehandLine') && p.length < 2) { setDrawKind(null); return; }
    if ((kind === 'polygon' || kind === 'freehandPolygon') && p.length < 3) { setDrawKind(null); return; }
    if (kind === 'curve' && p.length < 2) { setDrawKind(null); return; }
    const id = addAnnotation({
      kind,
      name: `${kindLabel(kind)} ${useSceneStore.getState().annotations.length + 1}`,
      points: p,
      planeMode,
      style: defaultStyleFor(kind),
      visible: true,
    });
    endGestureHistory();
    useSelectionStore.getState().selectAnnotation(id);
    setDrawKind(null);
  }, [drawKind, points, addAnnotation, setDrawKind, planeMode]);

  const addPoint = useCallback((pt: P3) => {
    if (!drawKind) return;
    const next = [...points, pt];
    const need = requiredClicks(drawKind);
    if (points.length === 0) beginGestureHistory();
    setPoints(next);
    if (need > 0 && next.length >= need) finish(next);
  }, [drawKind, points, finish]);

  // Esc 取消
  useEffect(() => {
    if (!drawKind) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (freehandRef.current?.active) { freehandRef.current = null; endGestureHistory(); }
        useEditorUiStore.getState().setAnnotationDrawKind(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawKind]);

  // 面板「完成」按钮 → finish（通过 window 事件通信，避免跨组件引用）
  useEffect(() => {
    const onFinish = () => finish();
    window.addEventListener('dtwince:annotation-finish', onFinish);
    return () => window.removeEventListener('dtwince:annotation-finish', onFinish);
  }, [finish]);

  // 调试/测试桥：吸附候选点（E2E 断言吸附落点用）
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__annotationSnapPoints = () => snapCandidates();
    return () => { delete (window as unknown as Record<string, unknown>).__annotationSnapPoints; };
  }, [snapCandidates]);

  // 自由线/自由面：指针抬起（含画布外）→ 抽稀落库
  useEffect(() => {
    if (!drawKind) return;
    const onUp = () => {
      const fh = freehandRef.current;
      if (!fh?.active) return;
      freehandRef.current = null;
      const pts = rdpSimplify(fh.raw, 0.4);
      const min = drawKind === 'freehandPolygon' ? 3 : 2;
      if (pts.length < min) { endGestureHistory(); setPoints([]); setCursor(null); return; }
      finish(pts);
    };
    window.addEventListener('pointerup', onUp);
    return () => window.removeEventListener('pointerup', onUp);
  }, [drawKind, finish]);

  if (!drawKind) return null;
  const freehand = drawKind === 'freehandLine' || drawKind === 'freehandPolygon';
  const previewOutline = points.length
    ? (freehand ? points : outlineFor(drawKind, cursor ? [...points, cursor] : points))
    : [];
  return (
    <>
      {/* 地面捕捉层（大平面，略高于地面 y=0.05 保证事件优先级；透明材质接收点击/移动） */}
      <mesh
        position={[0, 0.05, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onPointerMove={(e: any) => {
          const p = resolvePoint(e);
          setCursor(p);
          const fh = freehandRef.current;
          if (fh?.active) {
            const last = fh.raw[fh.raw.length - 1];
            if (Math.hypot(p[0] - last[0], p[2] - last[2]) > 0.25) {
              fh.raw.push(p);
              setPoints(fh.raw.slice());
            }
          }
        }}
        onPointerDown={(e: any) => {
          if (drawKind !== 'freehandLine' && drawKind !== 'freehandPolygon') return;
          e.stopPropagation();
          const p = resolvePoint(e);
          freehandRef.current = { active: true, raw: [p] };
          beginGestureHistory();
          setPoints([p]);
          setCursor(p);
        }}
        onClick={(e: any) => {
          e.stopPropagation();
          if (drawKind === 'freehandLine' || drawKind === 'freehandPolygon') return; // 拖拽式
          addPoint(resolvePoint(e));
        }}
        onDoubleClick={(e: any) => {
          e.stopPropagation();
          if (needsExplicitFinish(drawKind)) finish();
        }}
      >
        <planeGeometry args={[1e6, 1e6]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      {/* 预览 */}
      {previewOutline.length > 1 && (
        <Line points={previewOutline} color="#ffd666" lineWidth={2} />
      )}
      {points.map((p, i) => (
        <mesh key={i} position={p}>
          <sphereGeometry args={[0.35, 8, 8]} />
          <meshBasicMaterial color="#ffd666" />
        </mesh>
      ))}
      {cursor && !freehand && (
        <mesh position={cursor}>
          <sphereGeometry args={[0.35, 8, 8]} />
          <meshBasicMaterial color="#ffd666" />
        </mesh>
      )}
    </>
  );
}

export default function AnnotationLayer({ showHandles = true }: { showHandles?: boolean }) {
  const annotations = useSceneStore((s) => s.annotations);
  const selectedId = useSelectionStore((s) => s.annotationSelectedId);

  // 调试/测试桥（E2E）：标绘数量/选中/手柄数/选中点/动画进度/文本解析值
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__annotationState = () => {
      const st = useSceneStore.getState();
      const sel = useSelectionStore.getState().annotationSelectedId;
      const a = st.annotations.find((x) => x.id === sel) ?? null;
      return {
        count: st.annotations.length,
        selected: sel,
        drawKind: useEditorUiStore.getState().annotationDrawKind,
        drawPoints: drawPointsRef.length,
        handles: a && showHandles ? a.points.length : 0,
        points: a?.points ?? null,
        kinds: st.annotations.map((x) => x.kind),
        anim: st.annotations
          .filter((x) => x.animation?.grow || x.animation?.fade)
          .map((x) => ({ id: x.id, progress: Number(getAnnotationAnimProgress(x.id).toFixed(3)) })),
        texts: st.annotations
          .filter((x) => x.kind === 'text')
          .map((x) => ({ id: x.id, text: resolvedTextRegistry.get(x.id) ?? x.style.text?.content ?? '', color: resolvedColorRegistry.get(x.id) ?? null })),
      };
    };
    return () => { delete (window as unknown as Record<string, unknown>).__annotationState; };
  }, [showHandles]);

  return (
    <>
      {annotations.filter((a) => a.visible).map((a) => (
        <AnnotationItem key={a.id} a={a} selected={a.id === selectedId} showHandles={showHandles} />
      ))}
      {showHandles && <AnnotationDrawSurface />}
    </>
  );
}
